/**
 * Situational talk (data/npcTalk.ts): what a townsperson or key NPC says when they greet the player
 * or speak up now and then, picked for the moment: the hour and the weather, where they stand, the
 * player's school, karma, PK colour, wounds and realm, what has just happened (a bout won, a monster
 * killed beside them, a quest handed in, a fight between players, the world boss), the rumours of
 * the quest under way, and how they regard the player (systems/npcMemory.ts).
 *
 * The World asks through `folkLine` before its own lines (data/folk.ts); null leaves it to them, so
 * every person keeps the lines of their own work too. A line's gesture is played as an act for a
 * moment (systems/npcActs.ts `setAct`), and on the folk brain too when it is one of its greetings
 * (bow, wave, cower); the bark carries it as well. Every draw comes from the World's `Rng`; times
 * are the World's.
 */
import { FOLK, FOLKS } from '../data/folk';
import {
  ACT_VOICE,
  FOLK_ROLE,
  KEY_ROLE,
  MASTER_ROLE,
  TALK,
  type Standing,
  type TalkAct,
  type TalkGroup,
  type TalkOn,
  type TalkRecent,
  type TalkRole,
  type TalkWhen,
} from '../data/npcTalk';
import { getSchool } from '../data/schools';
import { rankFor, STAGES } from '../cultivation';
import { pkColor } from '../karma';
import type { Entity, World, WorldEvent } from '../world';
import { peopleNear, standingOf } from './npcMemory';
import { actRoleOf, setAct } from './npcActs';
import type { SystemHost, WorldSystem } from './types';

/** A line to say, and the gesture asked for with it. */
export interface TalkLine {
  text: string;
  act?: TalkAct;
}

/** Seconds a happening stays on people's lips. */
export const RECENT_FOR: Readonly<Record<TalkRecent, number>> = {
  wonDuel: 120,
  lostDuel: 90,
  questDone: 180,
  saved: 60,
  pkNear: 30,
  boss: 0,
  bossDown: 240,
};

/**
 * How often the World's own lines are left to speak instead, on a plain greeting or aside (nothing
 * about the player or the news matched, and the person thinks nothing special of the player).
 */
export const DEFER = { greet: 0.35, idle: 0.45, gift: 0 } as const satisfies Record<TalkOn, number>;

/** Conditions about the player or the news: a match on one of them is always said. */
const STRONG: readonly (keyof TalkWhen)[] = ['standing', 'karma', 'pk', 'hurt', 'recent'];

/** The gestures a folk brain plays itself (also set as acts), and how long a line's act is held (s). */
const OWN_GESTURES = new Set<TalkAct>(['bow', 'wave', 'cower']);
export const ACT_FOR = 2.4;
/** How much longer an act person waits between asides than a townsperson (`FOLK.barkEvery`). */
export const ACT_BARK_SLOWER = 2;

/** Everything a group's conditions are checked against. */
export interface TalkContext {
  role: TalkRole;
  period: string;
  part: string;
  weather: string;
  zone: string;
  at: string | null;
  standing: Standing;
  school: string | null;
  karma: string;
  pk: string;
  hurt: boolean;
  realm: number;
  recent: ReadonlySet<TalkRecent>;
  quest: string | null;
}

/** The role a person speaks in (data/npcTalk.ts), or null for someone who does not talk. */
export function roleOf(w: World, e: Entity): TalkRole | null {
  const f = e.folk;
  if (!f) {
    const act = actRoleOf(e);
    return act ? ACT_VOICE[act] : null;
  }
  if (!f.fixed) return FOLK_ROLE[f.id] ?? (FOLKS[f.id]?.child ? 'child' : 'farmer');
  if (f.id === 'master') {
    for (const [school, m] of w.schoolMasters) if (m === e) return MASTER_ROLE[school] ?? 'master';
    return 'master';
  }
  return KEY_ROLE[f.id] ?? null;
}

const has = <T>(xs: readonly T[] | undefined, v: T): boolean => !xs || xs.includes(v);

/** Whether every condition of a group holds. */
export function matches(when: TalkWhen | undefined, c: TalkContext): boolean {
  if (!when) return true;
  if (!has(when.period, c.period)) return false;
  if (!has(when.part as readonly string[] | undefined, c.part)) return false;
  if (!has(when.weather as readonly string[] | undefined, c.weather)) return false;
  if (!has(when.zone as readonly string[] | undefined, c.zone)) return false;
  if (when.at && (!c.at || !when.at.includes(c.at))) return false;
  if (!has(when.standing, c.standing)) return false;
  if (when.school !== undefined) {
    if (typeof when.school === 'boolean') {
      if (when.school !== !!c.school) return false;
    } else if (!c.school || !when.school.includes(c.school)) return false;
  }
  if (!has(when.karma as readonly string[] | undefined, c.karma)) return false;
  if (!has(when.pk as readonly string[] | undefined, c.pk)) return false;
  if (when.hurt !== undefined && when.hurt !== c.hurt) return false;
  if (when.realm && (c.realm < when.realm[0] || c.realm > when.realm[1])) return false;
  if (when.recent && !when.recent.some((r) => c.recent.has(r))) return false;
  if (when.quest && (!c.quest || !when.quest.includes(c.quest))) return false;
  return true;
}

/** How many conditions a group sets: the more specific, the likelier it is picked. */
export const specificity = (when: TalkWhen | undefined): number => (when ? Object.keys(when).length : 0);

/** The groups a person may draw from now, each with its weight. */
export function candidates(
  on: TalkOn,
  c: TalkContext,
  talk: readonly TalkGroup[] = TALK,
): [TalkGroup, number][] {
  const out: [TalkGroup, number][] = [];
  for (const g of talk) {
    if (!g.on.includes(on) || !g.lines.length) continue;
    if (g.who !== 'all' && !g.who.includes(c.role)) continue;
    if (!matches(g.when, c)) continue;
    // A voice's own lines count as one condition more than the lines everyone says.
    const k = specificity(g.when) + (g.who === 'all' ? 0 : 1);
    out.push([g, (g.w ?? 1) * (1 + 1.5 * k)]);
  }
  return out;
}

/** One line of the groups, drawn by weight with two draws from `rnd`. */
export function pickLine(groups: readonly [TalkGroup, number][], rnd: () => number): TalkLine | null {
  if (!groups.length) return null;
  let r = rnd() * groups.reduce((a, [, w]) => a + w, 0);
  let g = groups[groups.length - 1]![0];
  for (const [x, w] of groups)
    if ((r -= w) <= 0) {
      g = x;
      break;
    }
  const text = g.lines[Math.floor(rnd() * g.lines.length)]!;
  return g.act ? { text, act: g.act } : { text };
}

export class NpcTalkSystem implements WorldSystem {
  readonly name = 'npcTalk';
  /** When each happening last took place (World time); `saved` and `pkNear` by person id. */
  private readonly when = new Map<TalkRecent, number>();
  private readonly savedAt = new Map<number, number>();
  private readonly pkAt = new Map<number, number>();
  /** The act people (no folk brain: the World does not greet for them), their greeting and bark timers. */
  private actPeople: Entity[] = [];
  private listT = 0;
  private readonly greetAt = new Map<number, number>();
  private readonly barkAt = new Map<number, number>();

  constructor(private readonly h: SystemHost) {}

  /**
   * The act people greet a passer-by and speak up now and then, as the World has its townsfolk do
   * (data/folk.ts `FOLK` distances and spans). Draws from the Rng only when one of them speaks.
   */
  step(dt: number): void {
    const w = this.h.world;
    if ((this.listT -= dt) <= 0) {
      this.listT = 2;
      this.actPeople = [];
      for (const e of w.entities.values())
        if (!e.folk && e.kind === 'ambient' && actRoleOf(e)) this.actPeople.push(e);
    }
    const p = w.player;
    if (p.dead) return;
    for (const e of this.actPeople) {
      if (e.dead) continue;
      const d = Math.hypot(e.x - p.x, e.y - p.y);
      if (d > FOLK.hearing) continue;
      if (d < FOLK.notice) {
        if (w.time < (this.greetAt.get(e.id) ?? -Infinity)) continue;
        this.greetAt.set(e.id, w.time + FOLK.greetEvery);
        const l = this.line(e, 'greet', false);
        if (l) this.say(e, l);
        continue;
      }
      const at = this.barkAt.get(e.id);
      if (at === undefined) {
        // The first aside a while after the player comes within hearing.
        this.barkAt.set(e.id, w.time + FOLK.barkEvery[0] * ACT_BARK_SLOWER);
        continue;
      }
      if (w.time < at) continue;
      // Slower than the townsfolk: a square full of act people would otherwise talk all at once.
      const [lo, hi] = FOLK.barkEvery;
      this.barkAt.set(e.id, w.time + (lo + this.h.random() * (hi - lo)) * ACT_BARK_SLOWER);
      const l = this.line(e, 'idle', false);
      if (l) this.say(e, l);
    }
  }

  observe(ev: WorldEvent): void {
    const w = this.h.world;
    const p = w.player;
    switch (ev.type) {
      case 'ladder':
        if (ev.phase === 'result') this.when.set(ev.won ? 'wonDuel' : 'lostDuel', w.time);
        return;
      case 'quest':
        if (ev.status === 'completed') this.when.set('questDone', w.time);
        return;
      case 'bossDefeated':
        this.when.set('bossDown', w.time);
        return;
      case 'death': {
        const mon = w.get(ev.id);
        if (!mon || mon.kind !== 'monster' || p.dead) return;
        if (mon.targetId !== p.id && Math.hypot(mon.x - p.x, mon.y - p.y) > 6) return;
        for (const e of peopleNear(w, mon.x, mon.y, 9)) this.savedAt.set(e.id, w.time);
        return;
      }
      case 'hit': {
        if (ev.srcId !== p.id || w.duel?.phase === 'fight') return;
        const t = w.get(ev.tgtId);
        if (!t || t === p || (t.kind !== 'player' && t.kind !== 'rival')) return;
        for (const e of peopleNear(w, t.x, t.y, 13)) this.pkAt.set(e.id, w.time);
        return;
      }
      case 'npcGift': {
        const e = w.get(ev.id);
        if (!e) return;
        const line = this.line(e, 'gift');
        if (line) this.say(e, line);
        return;
      }
    }
  }

  folkLine(e: Entity, on: TalkOn): TalkLine | null {
    return this.line(e, on);
  }

  /** What has just happened, as this person knows it. */
  recent(e: Entity): Set<TalkRecent> {
    const w = this.h.world;
    const out = new Set<TalkRecent>();
    const fresh = (k: TalkRecent, t: number | undefined) => t !== undefined && w.time - t <= RECENT_FOR[k];
    for (const [k, t] of this.when) if (fresh(k, t)) out.add(k);
    if (fresh('saved', this.savedAt.get(e.id))) out.add('saved');
    if (fresh('pkNear', this.pkAt.get(e.id))) out.add('pkNear');
    if (w.worldBoss && !w.worldBoss.dead) out.add('boss');
    return out;
  }

  context(e: Entity): TalkContext | null {
    const w = this.h.world;
    const role = roleOf(w, e);
    if (!role) return null;
    const pr = w.progress;
    const p = w.player;
    const q = w.currentQuest();
    return {
      role,
      period: w.period.id,
      part: w.dayPart,
      weather: w.weather.kind,
      zone: w.map.zone,
      at: e.folk?.station?.at ?? null,
      standing: standingOf(w, e),
      school: pr.school,
      karma: pr.karma,
      pk: pkColor(pr.pkPoints),
      hurt: !p.dead && p.hp < p.maxHp * 0.4,
      realm: Math.floor(rankFor(pr.cultivationSpent).index / STAGES),
      recent: this.recent(e),
      quest: q && pr.quest.status === 'active' ? q.id : null,
    };
  }

  /** A line for this person to say now, or null to leave it to the World's own lines. */
  line(e: Entity, on: TalkOn, defer = true): TalkLine | null {
    const c = this.context(e);
    if (!c) return null;
    const groups = candidates(on, c);
    if (!groups.length) return null;
    // Nothing about the player or the news matched, and the person thinks nothing special of the
    // player: now and then their own words (the work at hand, the old greetings) are said instead.
    const plain =
      c.standing === 'neutral' && !groups.some(([g]) => STRONG.some((k) => g.when?.[k] !== undefined));
    if (defer && plain && this.h.random() < DEFER[on]) return null;
    const line = pickLine(groups, () => this.h.random());
    if (line?.act) this.gesture(e, line.act);
    return line;
  }

  /** The line's gesture: the folk brain's own greeting, and the act (systems/npcActs.ts `setAct`). */
  private gesture(e: Entity, act: TalkAct): void {
    if (e.folk && OWN_GESTURES.has(act)) {
      e.folk.gesture = act as 'bow' | 'wave' | 'cower';
      e.folk.gestureT = Math.max(e.folk.gestureT, 1.3);
    }
    setAct(this.h.world, e, act, ACT_FOR);
  }

  /** Says a line out of turn (thanks for a gift), as the World's barks are said. */
  private say(e: Entity, l: TalkLine): void {
    const pr = this.h.world.progress;
    const school = pr.school ? getSchool(pr.school).name : '';
    const text = l.text.replaceAll('{name}', pr.name).replaceAll('{school}', school);
    this.h.emit(l.act ? { type: 'bark', id: e.id, text, act: l.act } : { type: 'bark', id: e.id, text });
  }
}
